/**
 * useOrbitalBridge Hook
 *
 * Decoupled bridge for trait <-> server communication via the event bus.
 * Components emit events on the bus; the bridge forwards to the server
 * and emits responses back. Replaces the tightly-coupled useServerBridge.
 *
 * Lifecycle events emitted on the bus:
 *   - `{traitName}:DISPATCH`  - before server call (event, payload, currentState)
 *   - `{traitName}:{event}:SUCCESS` - after successful server response
 *   - `{traitName}:{event}:ERROR`   - after failed server response
 *
 * @generated by Orbital Compiler (Phase 5)
 * @packageDocumentation
 */

import { useCallback } from 'react';
import { useEventBus, useTranslate } from '@almadar/ui/hooks';
import type { EntityRow, EventPayload, EventPayloadValue } from '@almadar/core';
import type { EventResponse, EmittedEvent } from '@app/shared';
import { openBundledBrowserStore } from '@almadar/runtime';
import type { EventRequest } from '@app/shared';
import { browserCallService } from '@/browserServices';
import { authHeaders } from '../config/auth';

export type { EventRequest, EventResponse } from '@app/shared';

export interface OrbitalBridgeConfig {
  /** API base path for the trait (e.g., '/api/task-crud') */
  basePath: string;
  /** Owning orbital name — used to qualify bus keys per gap #13. */
  orbitalName: string;
  /** Trait name - used to qualify bus keys and namespace lifecycle events. */
  traitName: string;
}

export interface OrbitalBridge {
  /** Send an event to the server and emit lifecycle events on the bus */
  sendEvent: (
    event: string,
    payload?: EventPayload,
    currentState?: string,
    fields?: EntityRow
  ) => Promise<EventResponse>;
  /** Send an orbital event to the server over WebSocket (send-server operator). */
  sendServerEvent: (event: string, payload?: EventPayload) => void;
  /** The event bus instance (shared with cross-trait listeners) */
  eventBus: ReturnType<typeof useEventBus>;
}

// API base URL: empty in dev (Vite proxy handles /api), full server URL in production
const API_BASE = import.meta.env.VITE_API_URL ?? '';

let _ws: WebSocket | null = null;
const _wsQueue: string[] = [];
function _getOrOpenWs(): WebSocket | null {
  if (typeof WebSocket === 'undefined' || typeof location === 'undefined') return null;
  if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) return _ws;
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  try {
    _ws = new WebSocket(`${proto}//${location.host}/ws/events`);
    _ws.addEventListener('open', () => { _wsQueue.splice(0).forEach(m => _ws!.send(m)); });
    _ws.addEventListener('error', () => { _ws = null; });
    _ws.addEventListener('close', () => { _ws = null; });
  } catch { _ws = null; }
  return _ws;
}

// Live-push (Almadar_Live_Push.md): one id per tab, stamped on every
// dispatch. The server's SSE broadcast excludes the origin clientId —
// this tab already receives the emit in-response.
const _clientId = typeof crypto !== 'undefined' ? crypto.randomUUID() : '';

// Legs over browser-stored entities run against the in-process browser store.
const __BROWSER_LEGS: ReadonlySet<string> = new Set(['LandingARKflowLandingAnalytics\u0000INIT', 'LandingARKflowLandingAnalytics\u0000TRACK', 'LandingENKflowLandingAnalytics\u0000INIT', 'LandingENKflowLandingAnalytics\u0000TRACK', 'LandingSLKflowLandingAnalytics\u0000INIT', 'LandingSLKflowLandingAnalytics\u0000TRACK']);
import __messages_en from '../../../../locales/en.json';
import __messages_ar from '../../../../locales/ar.json';
import __messages_sl from '../../../../locales/sl.json';
const __BROWSER_MESSAGES: Record<string, Record<string, string>> = { en: __messages_en, ar: __messages_ar, sl: __messages_sl };
// One store per viewer locale: its rows are seeded in that locale.
const __browserTransports = new Map<string, ReturnType<typeof openBundledBrowserStore>>();
function __browserSend(orbitalName: string, request: EventRequest, locale: string): Promise<EventResponse> {
  let transport = __browserTransports.get(locale);
  if (transport === undefined) {
    transport = import('@/browserSchema.json').then((m) => openBundledBrowserStore(m.default, locale, browserCallService, __BROWSER_MESSAGES));
    __browserTransports.set(locale, transport);
  }
  return transport.then((t) => t.send(orbitalName, { ...request, locale }));
}

const __LIFECYCLE_EVENTS: ReadonlySet<string> = new Set(['INIT', 'LOAD', '$MOUNT']);
interface __MountRequest { trait: string; event: string; from?: string; fields?: EntityRow; payload?: EventPayload }
interface __MountItem extends __MountRequest { orbitalName: string; resolve: (r: EventResponse) => void; reject: (e: Error) => void }
const __mountBatches = new Map<string, __MountItem[]>();
// The page's locale (its I18nProvider); servers evaluate `i18n/t` with it.
let __viewerLocale: string | undefined;
/** The lifecycle sends of every hook mounting in one commit share ONE `mount` request to the orbital route. */
function __mountFetch(orbitalName: string, item: __MountRequest): Promise<EventResponse> {
  const key = `${orbitalName}\u0000${JSON.stringify(item.payload ?? null)}`;
  return new Promise<EventResponse>((resolve, reject) => {
    let batch = __mountBatches.get(key);
    if (batch === undefined) {
      batch = [];
      __mountBatches.set(key, batch);
      queueMicrotask(() => { void __flushMount(key); });
    }
    batch.push({ ...item, orbitalName, resolve, reject });
  });
}
async function __flushMount(key: string): Promise<void> {
  const batch = __mountBatches.get(key) ?? [];
  __mountBatches.delete(key);
  const first = batch[0];
  if (first === undefined) return;
  const entityByTrait: Record<string, EntityRow> = {};
  for (const i of batch) if (i.fields !== undefined) entityByTrait[i.trait] = i.fields;
  try {
    const response = await fetch(`${API_BASE}/api/orbitals/${first.orbitalName}/events`, {
      method: 'POST',
      headers: await authHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        event: first.event,
        payload: first.payload,
        clientId: _clientId,
        mount: batch.map((i) => ({ trait: i.trait, event: i.event })),
        traits: batch.flatMap((i) => (i.from !== undefined ? [{ trait: i.trait, from: i.from }] : [])),
        entityByTrait: Object.keys(entityByTrait).length > 0 ? entityByTrait : undefined,
        locale: __viewerLocale,
      }),
    });
    const result: EventResponse = response.ok
      ? await response.json()
      : { success: false, transitioned: false, states: {}, emittedEvents: [], error: `HTTP ${response.status}: ${response.statusText}` };
    // The cross-trait re-emits run once: only the first item's slice carries them.
    batch.forEach((i, index) => i.resolve(index === 0 ? result : { ...result, emittedEvents: [] }));
  } catch (err) {
    for (const i of batch) i.reject(err instanceof Error ? err : new Error(String(err)));
  }
}

// Shared qualified cascade re-emit — used both for the in-response
// `emittedEvents` loop below and the SSE subscribe leg, so a same-tab
// cascade and a cross-client broadcast fire through the identical
// `UI:${orbital}.${trait}.${event}` path that `listens` subscriptions match.
export function emitCascadeEvent(
  eventBus: ReturnType<typeof useEventBus>,
  orbitalName: string,
  traitName: string,
  ev: Pick<EmittedEvent, 'event' | 'payload' | 'source'>
): void {
  const evOrbital = ev.source?.orbital ?? orbitalName;
  const evTrait = ev.source?.trait ?? traitName;
  eventBus.emit(`UI:${evOrbital}.${evTrait}.${ev.event}`, ev.payload, { ...(ev.source ?? {}), orbital: evOrbital, trait: evTrait, fromBridge: true });
}

export function useOrbitalBridge({ basePath, orbitalName, traitName }: OrbitalBridgeConfig): OrbitalBridge {
  const eventBus = useEventBus();
  const { locale: __locale } = useTranslate();
  __viewerLocale = __locale;

  const sendEvent = useCallback(
    async (
      event: string,
      payload?: EventPayload,
      currentState?: string,
      fields?: EntityRow
    ): Promise<EventResponse> => {
      // Emit pre-dispatch lifecycle event (for debugging, analytics, local listeners)
      eventBus.emit(`${traitName}:DISPATCH`, { event, payload, currentState });

      try {
        const __browserLeg = __BROWSER_LEGS.has(`${traitName}\u0000${event}`);
        const __isMount = !__browserLeg && __LIFECYCLE_EVENTS.has(event);
        const response = (__isMount || __browserLeg) ? undefined : await fetch(`${API_BASE}${basePath}/events`, {
          method: 'POST',
          headers: await authHeaders(),
          credentials: 'include',
          body: JSON.stringify({ event, payload, clientId: _clientId, traits: [{ trait: traitName, from: currentState }], entityByTrait: fields ? { [traitName]: fields } : undefined, locale: __viewerLocale }),
        });

        if (response !== undefined && !response.ok) {
          const errorResponse: EventResponse = {
            success: false,
            transitioned: false,
            states: {},
            emittedEvents: [],
            error: `HTTP ${response.status}: ${response.statusText}`,
          };
          eventBus.emit(`${traitName}:${event}:ERROR`, { event, success: false, error: errorResponse.error });
          return errorResponse;
        }

        const result: EventResponse = __browserLeg
          ? await __browserSend(orbitalName, { event, payload, clientId: _clientId, ...(currentState !== undefined ? { traits: [{ trait: traitName, from: currentState }] } : {}), ...(fields ? { entityByTrait: { [traitName]: fields } } : {}) }, __locale)
          : response === undefined
          ? await __mountFetch(orbitalName, { trait: traitName, event, from: currentState, fields, payload })
          : await response.json();

        if (result.success) {
          eventBus.emit(`${traitName}:${event}:SUCCESS`, { event, currentState, success: true, newState: result.states?.[traitName], emittedEvents: result.emittedEvents.map((e) => ({ event: e.event, payload: e.payload }) as EventPayloadValue), effectResults: result.effectResults?.map((e) => ({ ...e }) as EventPayloadValue), data: result.data } as EventPayload);
          // Re-emit the transition's event on the client bus so cross-
          // trait `listens { SourceTrait EVENT → Y }` subscriptions fire.
          // Otherwise the bus only ever sees `UI:EVENT` (from Button) and
          // `${traitName}:${event}:SUCCESS` (logging) — cross-trait listeners
          // subscribe to plain `EVENT` with a source filter, so they'd never
          // fire on a user-click flow even when the server returns success.
          // Skip lifecycle events to avoid re-dispatching INIT cascades.
          if (event !== 'INIT' && event !== 'LOAD' && event !== '$MOUNT' && event !== '$UNMOUNT') {
            // Gap #13: emit qualified `UI:Orbital.Trait.Event` so cross-trait
            // `listens` subscriptions (which now subscribe to the qualified
            // key) match. `fromBridge: true` is kept on `source` for diagnostic
            // tooling that wants to distinguish bus echoes from user clicks.
            eventBus.emit(`UI:${orbitalName}.${traitName}.${event}`, payload, { orbital: orbitalName, trait: traitName, fromBridge: true, dispatched: true });
          }
          // Fix B (Phase 9.5.H): server is stateless per request. Any effect
          // with `emit: { success: "X" }` appends X to `result.emittedEvents`.
          // Fire each on the client bus; cross-trait `listens` subscribers
          // (gap #13: qualified bus keys) dispatch them back to their state
          // machines so the state cascade advances. Without this, a
          // fetch-in-INIT pattern stalls in `loading`.
          for (const ev of result.emittedEvents) {
            emitCascadeEvent(eventBus, orbitalName, traitName, ev);
          }
        } else {
          eventBus.emit(`${traitName}:${event}:ERROR`, { event, currentState, success: false, error: result.error, guardFailed: result.guardFailed });
        }

        return result;
      } catch (err) {
        const errorResponse: EventResponse = {
          success: false,
          transitioned: false,
          states: {},
          emittedEvents: [],
          error: err instanceof Error ? err.message : 'Network error',
        };
        eventBus.emit(`${traitName}:${event}:ERROR`, { event, success: false, error: errorResponse.error });
        return errorResponse;
      }
    },
    [basePath, traitName, eventBus, __locale]
  );

  const sendServerEvent = useCallback((event: string, payload?: EventPayload): void => {
    const msg = JSON.stringify({ type: 'ORBITAL_EVENT', payload: { orbital: orbitalName, event, payload: payload ?? null } });
    const ws = _getOrOpenWs();
    if (!ws) return;
    if (ws.readyState === WebSocket.OPEN) { ws.send(msg); } else { _wsQueue.push(msg); }
  }, [orbitalName]);

  return { sendEvent, sendServerEvent, eventBus };
}

export default useOrbitalBridge;
